// Minimal Markdown → HTML renderer, ported verbatim from v1 dashboard's `app.js`.
//
// Supports: fenced code blocks, horizontal rules, ATX headings (h1-h6), tables
// with column alignment, blockquotes (recursive), unordered/ordered lists,
// paragraphs, and inline: code spans, bold (**…**), italics (*…*), links.
//
// Output is rendered into the DOM via `v-html` from `ScorecardTab.vue`. Every
// branch escapes input through `escapeHtml` first, so user-supplied markdown
// cannot inject script tags.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function inline(s: string): string {
  let out = escapeHtml(s);
  out = out.replace(/`([^`]+?)`/g, (_m, c: string) => `<code>${c}</code>`);
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener">$1</a>',
  );
  return out;
}

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|");
}

export function renderMarkdown(src: string): string {
  const lines = String(src).replace(/\r\n/g, "\n").split("\n");
  let html = "";
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      html += `<pre><code class="lang-${escapeHtml(lang)}">${escapeHtml(
        buf.join("\n"),
      )}</code></pre>`;
      continue;
    }

    // hr
    if (/^\s*---+\s*$/.test(line)) {
      html += "<hr>";
      i++;
      continue;
    }

    // heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const depth = h[1].length;
      html += `<h${depth}>${inline(h[2])}</h${depth}>`;
      i++;
      continue;
    }

    // table
    if (
      /\|/.test(line) &&
      i + 1 < lines.length &&
      /^\s*\|?\s*[:\- |]+\|[:\- |]+\s*$/.test(lines[i + 1])
    ) {
      const headerCells = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map((c) => {
        const t = c.trim();
        if (/^:.*:$/.test(t)) return "center";
        if (/:$/.test(t)) return "right";
        return "left";
      });
      i += 2;
      let tbl =
        "<table><thead><tr>" +
        headerCells
          .map(
            (c, idx) =>
              `<th class="${aligns[idx] === "right" ? "num" : ""}" style="text-align:${aligns[idx]};">${inline(c.trim())}</th>`,
          )
          .join("") +
        "</tr></thead><tbody>";
      while (i < lines.length && /\|/.test(lines[i]) && lines[i].trim() !== "") {
        const cells = splitTableRow(lines[i]);
        tbl +=
          "<tr>" +
          cells
            .map(
              (c, idx) =>
                `<td class="${aligns[idx] === "right" ? "num" : ""}" style="text-align:${aligns[idx] || "left"};">${inline(c.trim())}</td>`,
            )
            .join("") +
          "</tr>";
        i++;
      }
      tbl += "</tbody></table>";
      html += tbl;
      continue;
    }

    // blockquote
    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      html += `<blockquote>${renderMarkdown(buf.join("\n"))}</blockquote>`;
      continue;
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      let out = "<ul>";
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        out += `<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>`;
        i++;
      }
      out += "</ul>";
      html += out;
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      let out = "<ol>";
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        out += `<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>`;
        i++;
      }
      out += "</ol>";
      html += out;
      continue;
    }

    // blank line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // paragraph (gather until blank)
    const buf: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,6}\s|>|```|\s*[-*]\s+|\s*\d+\.\s+|\s*---+\s*$)/.test(lines[i]) &&
      !(
        /\|/.test(lines[i]) &&
        i + 1 < lines.length &&
        /^\s*\|?\s*[:\- |]+\|[:\- |]+\s*$/.test(lines[i + 1])
      )
    ) {
      buf.push(lines[i]);
      i++;
    }
    html += `<p>${inline(buf.join(" "))}</p>`;
  }
  return html;
}
